import React, { createContext, useContext, useReducer, useEffect } from 'react';

// Начално състояние на кошницата
const initialState = {
  items: [],
  total: 0,
  subtotal: 0,
  shippingCost: 0,
  shippingMethod: null,
  coupon: null,
  discountAmount: 0
};

// Създаване на контекст
const CartContext = createContext();

// Редуктор за кошницата
const cartReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_ITEM': {
      const existingItemIndex = state.items.findIndex(item => item.id === action.payload.id);
      
      if (existingItemIndex >= 0) {
        // Продуктът съществува, обновяване на количеството
        const updatedItems = [...state.items];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: updatedItems[existingItemIndex].quantity + action.payload.quantity
        };
        
        return calculateTotals({
          ...state,
          items: updatedItems
        });
      } else {
        // Нов продукт, добавяне в кошницата
        return calculateTotals({
          ...state,
          items: [...state.items, action.payload]
        });
      }
    }
    
    case 'UPDATE_QUANTITY': {
      const { id, quantity } = action.payload;
      
      if (quantity <= 0) {
        // Премахване на продукта, ако количеството е 0 или отрицателно
        return calculateTotals({
          ...state,
          items: state.items.filter(item => item.id !== id)
        });
      }
      
      // Обновяване на количеството
      const updatedItems = state.items.map(item => 
        item.id === id ? { ...item, quantity } : item
      );
      
      return calculateTotals({
        ...state,
        items: updatedItems
      });
    }
    
    case 'REMOVE_ITEM': {
      return calculateTotals({
        ...state,
        items: state.items.filter(item => item.id !== action.payload)
      });
    }
    
    case 'SET_SHIPPING_METHOD': {
      return calculateTotals({
        ...state,
        shippingMethod: action.payload
      });
    }

    case 'APPLY_COUPON': {
      return calculateTotals({
        ...state,
        coupon: action.payload,
        discountAmount: parseFloat(action.payload.discount_amount || 0)
      });
    }

    case 'REMOVE_COUPON': {
      return calculateTotals({
        ...state,
        coupon: null,
        discountAmount: 0
      });
    }
    
    case 'CLEAR_CART': {
      return initialState;
    }
    
    default:
      return state;
  }
};

// Помощна функция за изчисляване на общите суми
const calculateTotals = (state) => {
  const subtotal = state.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const shippingCost = state.shippingMethod ? state.shippingMethod.cost : 0;
  
  // Преизчисляване на отстъпката, ако има купон
  let discountAmount = state.discountAmount;
  if (state.coupon) {
    // Преизчисляване според типа на купона
    const coupon = state.coupon;
    
    switch (coupon.discount_type) {
      case 'percent':
        discountAmount = subtotal * (parseFloat(coupon.amount) / 100);
        break;
      case 'fixed_cart':
        discountAmount = parseFloat(coupon.amount);
        break;
      case 'fixed_product':
        // За опростяване, прилагане към общата сума на кошницата
        discountAmount = parseFloat(coupon.amount);
        break;
      default:
        discountAmount = parseFloat(coupon.discount_amount || 0);
    }
    
    // Гарантиране, че отстъпката не надвишава междинната сума
    discountAmount = Math.min(discountAmount, subtotal);
  }
  
  const total = subtotal + shippingCost - discountAmount;
  
  return {
    ...state,
    subtotal,
    shippingCost,
    discountAmount,
    total
  };
};

// Компонент за доставчик на кошницата
export const CartProvider = ({ children }) => {
  // Зареждане на кошницата от localStorage, ако е налична
  const loadInitialState = () => {
    try {
      const savedCart = localStorage.getItem('cart');
      return savedCart ? JSON.parse(savedCart) : initialState;
    } catch (error) {
      console.error('Неуспешно зареждане на кошницата от localStorage:', error);
      return initialState;
    }
  };
  
  const [state, dispatch] = useReducer(cartReducer, null, loadInitialState);
  
  // Запазване на кошницата в localStorage при всяка промяна
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(state));
  }, [state]);
  
  return (
    <CartContext.Provider value={{ cart: state, dispatch }}>
      {children}
    </CartContext.Provider>
  );
};

// Потребителски хук за използване на контекста на кошницата
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart трябва да се използва в рамките на CartProvider');
  }
  return context;
};

export default CartContext; 